import React, { useState, useEffect } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';
import styles from './Result.module.css';

// Helper function to get card image from card code
const getCardImage = (cardCode) => {
  if (!cardCode) return null;
  try {
    if (cardCode.length < 2) {
      return null;
    }
    return `/assets/img/cards/${cardCode}.png`;
  } catch (e) {
    return null;
  }
};

// Parse AB20 cards - separate Andar and Bahar cards from card string
const parseAB20Cards = (cardString, aall = "", ball = "") => {
  if (!cardString) return { andarCards: [], baharCards: [] };

  const allCards = cardString.split(',').map(c => c.trim()).filter(c => c);

  // If aall and ball are provided, use them to separate cards
  let andarCardCodes = [];
  let baharCardCodes = [];

  if (aall && ball) {
    andarCardCodes = aall.split(',').map(c => c.trim()).filter(c => c);
    baharCardCodes = ball.split(',').map(c => c.trim()).filter(c => c);
  } else {
    // Fallback: alternate distribution (first card to Andar, second to Bahar, etc.)
    allCards.forEach((cardCode, index) => {
      if (index % 2 === 0) {
        andarCardCodes.push(cardCode);
      } else {
        baharCardCodes.push(cardCode);
      }
    });
  }

  const andarCards = andarCardCodes.map(code => ({
    code,
    image: getCardImage(code)
  }));

  const baharCards = baharCardCodes.map(code => ({
    code,
    image: getCardImage(code)
  }));

  return { andarCards, baharCards };
};

// Parse AB20 result - extract winner (Andar or Bahar)
const parseRdesc = (rdesc) => {
  if (!rdesc) return { winner: "" };

  // rdesc format: "Andar" or "Bahar"
  return {
    winner: rdesc.trim() || ""
  };
};

const ResultAB20 = ({
  data = [],
  gameData = {},
  gameType = 'ab20',
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(gameType);
        if (response?.success && response?.data?.data?.res) {
          setLastResults(response.data.data.res);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [gameType]);

  const handleResultClick = async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);

    const mid = resultItem?.mid;

    if (mid) {
      try {
        const response = await getDetailResults(gameType, mid.toString());
        if (response?.success && response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }

    setLoading(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Prepare modal data
  const getModalData = () => {
    if (!selectedResult) return null;

    // Get aall and ball from gameData or detailResult
    const aall = gameData?.data?.data?.aall || detailResult?.aall || '';
    const ball = gameData?.data?.data?.ball || detailResult?.ball || '';

    // Parse winner from rdesc or win value
    const rdesc = detailResult?.rdesc || selectedResult?.rdesc || "";
    const winValue = detailResult?.win || selectedResult?.win || "";
    const winnat = detailResult?.winnat || "";

    let winner = "";
    if (rdesc) {
      const parsed = parseRdesc(rdesc);
      winner = parsed.winner;
    } else if (winnat) {
      winner = winnat;
    } else if (winValue) {
      // Map win value: "1" or "A" -> "Andar", "2" or "B" -> "Bahar"
      if (winValue === "1" || winValue === "A" || winValue.toLowerCase() === "a") {
        winner = "Andar";
      } else if (winValue === "2" || winValue === "B" || winValue.toLowerCase() === "b") {
        winner = "Bahar";
      } else {
        winner = winValue;
      }
    }

    // If detailResult is not loaded yet, return basic data
    if (!detailResult) {
      return {
        roundId: selectedResult.mid || "N/A",
        matchTime: selectedResult.mt || new Date().toLocaleString(),
        andarCards: [],
        baharCards: [],
        winner: winner || "",
        rdesc: rdesc || ""
      };
    }

    const cardData = parseAB20Cards(detailResult.card || selectedResult.card, aall, ball);

    return {
      roundId: detailResult.rid || selectedResult.mid || "N/A",
      matchTime: detailResult.mtime || selectedResult.mt || new Date().toLocaleString(),
      andarCards: cardData.andarCards,
      baharCards: cardData.baharCards,
      winner: winner || "",
      rdesc: rdesc || ""
    };
  };

  const modalData = getModalData();

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults
    : (data && data.length > 0
      ? data.map((res, idx) => ({ win: res, mid: null, rdesc: res }))
      : []);

  return (
    <>
      {/* Last Results Section */}
      {allResults.length > 0 && (
        <div className={styles.lastResults}>
          {allResults.map((resultItem, index) => {
            const winValue = resultItem.win;
            // Map values: "1" or "A" -> "A", "2" or "B" -> "B", "R" or anything else -> display as is
            let displayValue = winValue;
            if (winValue === "1" || winValue === "A") {
              displayValue = "A";
            } else if (winValue === "2" || winValue === "B") {
              displayValue = "B";
            } else if (winValue === "R") {
              displayValue = "R";
            }
            // Default to showing the value as-is if it's not 1, 2, A, B, or R

            return (
              <span
                key={`${resultItem.mid || index}-${index}`}
                className={`${styles.resultCircle} ${displayValue === "A" ? styles.andar :
                    displayValue === "B" ? styles.bahar :
                      styles.andar
                  }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Clicking result:', resultItem, 'Index:', index);
                  handleResultClick(resultItem, index);
                }}
                style={{ cursor: 'pointer' }}
              >
                {displayValue}
              </span>
            );
          })}
        </div>
      )}

      {showModal && modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="modal-body">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <h6 className="text-right">
                    <b>Round Id :</b> {modalData.roundId}
                  </h6>
                </div>
              </div>
              <div className="row bottom-border-casino m-t-10">
                <div className="col-md-12">
                  {/* Andar Cards */}
                  {modalData.andarCards && modalData.andarCards.length > 0 && (
                    <div className="m-b-20">
                      <h6 className="text-center">Andar</h6>
                      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '5px' }}>
                        {modalData.andarCards.map((card, index) => (
                          <img
                            key={`andar-${index}`}
                            src={card.image || `/assets/img/cards/patti_back.png`}
                            alt={card.code}
                            style={{ height: '80px', width: 'auto' }}
                            onError={(e) => {
                              e.target.src = `/assets/img/cards/patti_back.png`;
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Bahar Cards */}
                  {modalData.baharCards && modalData.baharCards.length > 0 && (
                    <div className="m-b-20">
                      <h6 className="text-center">Bahar</h6>
                      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '5px' }}>
                        {modalData.baharCards.map((card, index) => (
                          <img
                            key={`bahar-${index}`}
                            src={card.image || `/assets/img/cards/patti_back.png`}
                            alt={card.code}
                            style={{ height: '80px', width: 'auto' }}
                            onError={(e) => {
                              e.target.src = `/assets/img/cards/patti_back.png`;
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Winner */}
                  <div className="col-md-12 text-left">
                    <p className="m-b-0 m-t-20">
                      <b>Winner:</b> {modalData.winner || "0"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultAB20;

